import { useContext } from 'react'
import { Formik } from 'formik'

import { ModalWrapper } from '../modal-wrapper'
import { MediaInputForm } from './media-input-form.component'
import { IMediaFileWithId } from 'src/types'
import { MediaInputContext } from './context/media.context'

import { mediaInputSchema } from 'src/common/validations'
import {
  audioFormatRegexp,
  imageFormatRegexp,
  pdfFormatRegexp,
  videoFormatRegexp,
} from 'src/constants'
import useStyles from './styles'

interface IMediaInputModalProps {
  open: boolean
  onClose: () => void
  isEdit?: boolean
  mainImageSwitch?: boolean
  showSwitch?: boolean
  noJourneyOptions?: boolean
  scanToEarnMode?: boolean
}

const MediaInputModal = ({
  open,
  onClose,
  isEdit,
  mainImageSwitch,
  noJourneyOptions,
  showSwitch,
  scanToEarnMode,
}: IMediaInputModalProps) => {
  const { image, setMedia, media } = useContext(MediaInputContext)
  const classes = useStyles({})

  const handleSubmit = (values: IMediaFileWithId) => {
    onClose()
    if (isEdit) {
      if (!values) return
      const prevIndex = media?.findIndex((item) => item.link === values.editId)

      // If link has been updated
      if (typeof prevIndex !== 'undefined' && prevIndex !== -1 && values.link) {
        const mediaCopy = Array.from(media ?? [])
        mediaCopy.splice(prevIndex, 1, values)
        return setMedia(mediaCopy)
      }

      // If link has been removed
      if (!values.link) {
        return setMedia(media?.filter((item) => item.link !== values.editId) ?? [])
      }

      // Default case
      setMedia(
        media?.map((item) => {
          if (item.link === values.editId) {
            return values
          }
          return item
        }) ?? [],
      )
    } else {
      if (!values || !values.link) return

      // If link already exists
      if (media?.find((item) => item.link === values.link)) return

      if (!values.type) {
        const type = values.link.split('.').pop()

        if (pdfFormatRegexp.test(values.link)) {
          values.type = 'application/pdf'
        } else if (audioFormatRegexp.test(values.link)) {
          values.type = 'audio/' + type
        } else if (videoFormatRegexp.test(values.link)) {
          values.type = 'video/' + type
        } else if (imageFormatRegexp.test(values.link)) {
          values.type = 'image/' + type
        } else {
          values.type = 'url'
        }
      }

      setMedia([...(media ?? []), values])
    }
  }

  const intialValues: IMediaFileWithId = {
    main_image: image?.main_image ?? false,
    show_item: image?.show_item ?? false,
    link_small: image?.link_small || null,
    link_medium: image?.link_medium || null,
    link_large: image?.link_large || null,
    editId: image?.editId,
    title: image?.title ?? '',
    link: image?.link ?? '',
    type: image?.type ?? '',
    file: image?.file ?? null,
    dragId: `id-${Date.now()}`,
  }

  return (
    <ModalWrapper onClose={onClose} open={open} className={classes.modalWrapper}>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
        initialValues={intialValues}
        validationSchema={mediaInputSchema}
      >
        <MediaInputForm
          mainImageSwitch={mainImageSwitch}
          noJourneyOptions={noJourneyOptions}
          showSwitch={showSwitch}
          onClose={onClose}
          scanToEarnMode={scanToEarnMode}
        />
      </Formik>
    </ModalWrapper>
  )
}

export { MediaInputModal }
